import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { DataTable } from '@adornis/data-view/client/table/data-table.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import {
  subscribeContactEntityCommentsPaginated,
  subscribeIntercomEntityCommentsPaginated,
  subscribeOdooEntityCommentsPaginated,
} from '../../api/comments.js';
import { CommentHelperEntity } from '../../entities/odoo.js';
import { CommentDetailView } from './CommentDetailView.js';

type EntityType = 'odoo' | 'intercom' | 'contact';

export const CommentDashboard = stateful(() => {
  const [selectedItem, setSelectedItem] = useState<null | { source: string; id: string }>(null);
  const [activeTab, _setActiveTab] = useState<EntityType>('odoo');
  const [isLoading, setIsLoading] = useState(false);

  const setActiveTab = (tab: EntityType) => {
    _setActiveTab(tab);
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 250);
  };

  const getOperationForTab = (tab: EntityType) => {
    switch (tab) {
      case 'odoo':
        return subscribeOdooEntityCommentsPaginated;
      case 'intercom':
        return subscribeIntercomEntityCommentsPaginated;
      case 'contact':
        return subscribeContactEntityCommentsPaginated;
    }
  };

  const columns: ColumnDefinition<typeof CommentHelperEntity>[] = [
    {
      fieldName: '_id',
      columnName: 'Entity Key',
      columnWidth: '200px',
    },
    // {
    //   fieldName: 'sourceID',
    //   columnName: 'Source ID',
    //   columnWidth: '200px',
    // },
    {
      fieldName: 'odooTicketID',
      columnName: 'Odoo Ticket ID',
      columnWidth: '150px',
    },
    {
      fieldName: 'comment.text',
      columnName: 'Comments',
      renderItem: (row: any) =>
        html`${row.comment?.text
          ? row.comment.text
          : html`<span class="opacity-60">No ${activeTab === 'contact' ? 'notes' : 'comment'}</span>`}`,
    },
    { fieldName: 'comment.authorID', columnName: 'AuthorID', columnWidth: '200px' },
    {
      fieldName: 'comment.createdAt',
      columnName: 'Comment Creation',
      columnWidth: '150px',
      getFieldMapping: odooEntity =>
        odooEntity.comment?.createdAt ? format(odooEntity.comment.createdAt, 'dd.MM.yyyy HH:mm') : '',
    },
    {
      customID: '_options',
      columnName: '',
      columnWidth: '50px',
      selectionSet: { sourceID: 1 },
      renderItem: (row: any) => html`
        <button
          class="btn btn-ghost btn-sm btn-circle"
          title="View Details"
          @click=${() => setSelectedItem({ source: activeTab.toUpperCase(), id: row.sourceID })}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-5 h-5"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
            />
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>
      `,
    },
  ];

  const odooEntityOptions = buildFullOptions<typeof CommentHelperEntity>({
    class: CommentHelperEntity,
    operation: getOperationForTab('odoo'),
    customColumns: columns,
    isSortable: false,
    // Provide a client-side view. The DataTable library supports View with an AdornisFilter.
    // The underlying source here is a helper query; the table renders against the loaded list.
    view: new View({
      filter: new AdornisFilter({}),
    }),
  });

  const intercomEntityOptions = buildFullOptions<typeof CommentHelperEntity>({
    class: CommentHelperEntity,
    operation: getOperationForTab('intercom'),
    customColumns: columns,
    isSortable: false,
    // Provide a client-side view. The DataTable library supports View with an AdornisFilter.
    // The underlying source here is a helper query; the table renders against the loaded list.
    view: new View({
      filter: new AdornisFilter({}),
    }),
  });

  const contactEntityOptions = buildFullOptions<typeof CommentHelperEntity>({
    class: CommentHelperEntity,
    operation: getOperationForTab('contact'),
    customColumns: columns,
    isSortable: false,
    // Provide a client-side view. The DataTable library supports View with an AdornisFilter.
    // The underlying source here is a helper query; the table renders against the loaded list.
    view: new View({
      filter: new AdornisFilter({}),
    }),
  });

  return html`
    <div class="flex gap-4 h-full p-6">
      <div
        class="flex-1 card bg-base-100 shadow-xl border border-base-300 overflow-auto ${selectedItem
          ? 'w-2/3'
          : 'w-full'}"
      >
        <div class="card-body">
          <div class="flex justify-between items-center mb-2 sticky top-0 bg-base-100 z-10 py-2">
            <h2 class="card-title text-2xl font-bold text-secondary">Comments</h2>
          </div>

          <!-- Tab Bar -->
          <div role="tablist" class="tabs tabs-boxed mb-4">
            <button
              role="tab"
              class="tab ${activeTab === 'odoo' ? 'tab-active' : ''}"
              @click=${() => {
                setActiveTab('odoo');
                setSelectedItem(null);
              }}
            >
              Odoo Tickets
            </button>
            <button
              role="tab"
              class="tab ${activeTab === 'intercom' ? 'tab-active' : ''}"
              @click=${() => {
                setActiveTab('intercom');
                setSelectedItem(null);
              }}
            >
              Intercom Conversations
            </button>
            <button
              role="tab"
              class="tab ${activeTab === 'contact' ? 'tab-active' : ''}"
              @click=${() => {
                setActiveTab('contact');
                setSelectedItem(null);
              }}
            >
              Contacts
            </button>
          </div>

          <div class="overflow-x-auto min-h-[200px]">
            ${isLoading
              ? html`<div class="flex justify-center items-center min-h-[200px]">
                  <span class="loading loading-spinner loading-lg"></span>
                </div>`
              : {
                  odoo: DataTable({ options: odooEntityOptions.dataViewOptions }),
                  intercom: DataTable({ options: intercomEntityOptions.dataViewOptions }),
                  contact: DataTable({ options: contactEntityOptions.dataViewOptions }),
                }[activeTab]}
          </div>
        </div>
      </div>

      ${selectedItem
        ? html`<div class="w-1/3">
            ${CommentDetailView({ selected: selectedItem, onClose: () => setSelectedItem(null) })}
          </div>`
        : nothing}
    </div>
  `;
});
